import Flex from '@Components/Bases/Flex';
import SearchCheckboxList from '@Components/SearchCheckboxList';
import {
  IInfoCategoryMasterData,
  useGetInfoCategoryMasterData,
} from '@Domains/MasterData';
import { Alert, Box, InputLabel, Typography } from '@mui/material';
import { IGreenhouseFormValues } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider/types';
import { useEffect, useState } from 'react';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';
import FormCheckboxList from './FormCheckboxList';
import InputOtherSectionSubcommittees from './InputOtherSection';
import Tags from './Tags';
import { OTHER_SUBCOMMITTEE_MASTER_DATA } from './utils';

type SubcommitteesCheckboxListProps = {
  name: string;
  subtitle?: string;
  selectTitle?: string;
  otherTitle?: string;
  required?: boolean;
  textAlign?: string;
  otherName: string;
  control: Control<IGreenhouseFormValues, unknown>;
  mode: 'create' | 'edit';
};

const SubcommitteesCheckboxList = ({
  subtitle,
  selectTitle,
  required,
  textAlign,
  otherName,
  otherTitle,
  name,
  control,
  mode,
}: SubcommitteesCheckboxListProps) => {
  const [{ data: subCommittees = [], loading, error }] =
    useGetInfoCategoryMasterData(INFO_CATEGORY_MASTER_DATA.SUB_COMMITTEE_ROLE);
  const [filterText, setFilterText] = useState<string>('');
  const [selectedSubcommittees, setSelectedSubcommittees] = useState<string[]>(
    []
  );
  const [editingIds, setEditingIds] = useState<Set<number>>(new Set());

  const subcommitteeDetails =
    useWatch({
      name: 'climateChangeStructure.subcommitteeResponsibility.subcommittees',
      control,
    }) ?? [];

  const [isOtherEnabled, setIsOtherEnabled] = useState<boolean>(false);
  const { watch, getValues, setValue } = useFormContext();

  const currentSubcommitteeRoles = watch(name);

  const subcommitteeType = watch('subcommitteeType');
  const isSubcommitteeTypeEmpty = subcommitteeType === '';

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  const handleDeleteTag = (subcommittees: string) => {
    setSelectedSubcommittees((prevState) => {
      const updatedSubcommittees = prevState.filter(
        (name) => name !== subcommittees
      );
      const isOtherSelected = updatedSubcommittees.includes('อื่น ๆ');
      setIsOtherEnabled(isOtherSelected);
      return updatedSubcommittees;
    });

    const updatedFormValues = getValues(name)?.filter(
      (data: IInfoCategoryMasterData) => data.nameTh !== subcommittees
    );
    setValue(name, updatedFormValues);
  };

  const handleCheckboxChange = (nameTh: string, checked: boolean) => {
    const currentValues = getValues(name) || [];

    if (checked) {
      const newValue = subCommittees.find((item) => item.nameTh === nameTh);
      if (newValue) {
        setValue(name, [...currentValues, newValue]);
      }
      setSelectedSubcommittees((prev) => [...prev, nameTh]);
    } else {
      setSelectedSubcommittees((prev) =>
        prev.filter((item) => item !== nameTh)
      );
      const updatedFormValues = currentValues.filter(
        (item: IInfoCategoryMasterData) => item.nameTh !== nameTh
      );
      setValue(name, updatedFormValues);
    }
  };

  const selectedSubcommitteeIds = new Set(
    subcommitteeDetails.flatMap(
      (detail) =>
        detail.subcommitteeRoles?.map(
          (role) => role?.infoCategoryMasterDataId
        ) || []
    )
  );

  useEffect(() => {
    if (mode === 'edit') {
      const currentSubcommitteeIds: Set<number> = new Set(
        currentSubcommitteeRoles
          .filter(
            (role: IInfoCategoryMasterData) =>
              role && (role.infoCategoryMasterDataId || role.id)
          )
          .map(
            (role: IInfoCategoryMasterData) =>
              role.infoCategoryMasterDataId || role.id
          )
      );
      setEditingIds(currentSubcommitteeIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSubcommittees = subCommittees.filter((sub) => {
    const isOtherSubcommittee =
      sub.id === OTHER_SUBCOMMITTEE_MASTER_DATA.infoCategoryMasterDataId;
    const isSelectedInOtherSubcommittees = selectedSubcommitteeIds.has(sub.id);

    if (mode === 'edit') {
      const isInEditMode = editingIds.has(sub.id);
      return (
        isInEditMode || isOtherSubcommittee || !isSelectedInOtherSubcommittees
      );
    }

    return isOtherSubcommittee || !isSelectedInOtherSubcommittees;
  });

  const subcommitteesList = filteredSubcommittees.filter((sub) =>
    sub.nameTh.toLowerCase().includes(filterText.toLowerCase())
  );

  const completedSection =
    useWatch({
      name: 'climateChangeStructure.completedSection',
      control,
    }) || undefined;

  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  useEffect(() => {
    const subcommitteesNameTh =
      watch(name)?.map(
        (subcommittees: IInfoCategoryMasterData) => subcommittees.nameTh
      ) || [];
    setSelectedSubcommittees(subcommitteesNameTh);
    const isOtherSelected = subcommitteesNameTh.includes('อื่น ๆ');
    setIsOtherEnabled(isOtherSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch(name)]);

  if (loading) {
    return (
      <Box mt={2}>
        <Typography>Loading ...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity={'error'}>
          เกิดข้อผิดพลาด ไม่สามารถโหลดข้อมูล บทบาทหน้าที่ของคณะกรรมการชุดย่อย
          ได้
        </Alert>
      </Box>
    );
  }

  return (
    <Flex justifyContent="end" gap={2}>
      <Flex maxWidth={150} flexShrink={0}>
        <InputLabel
          sx={{
            textAlign: `${textAlign}`,
            whiteSpace: 'pre-wrap',
            width: 1,
          }}
          required={required}
        >
          {subtitle}
        </InputLabel>
      </Flex>

      <Flex flexGrow={1} justifyContent={'flex-end'} maxWidth={600}>
        <Box flexDirection={'column'} width={1}>
          <Tags
            disabled={isCompletedEn || isCompletedTh || !subcommitteeType}
            value={selectedSubcommittees}
            onDeleteTag={handleDeleteTag}
          />
          <Typography my={2}>{selectTitle} (เลือกได้หลายข้อ)</Typography>
          <SearchCheckboxList
            filterText={filterText}
            handleChange={handleFilterChange}
          />
          <FormCheckboxList
            disabled={isCompletedEn || isCompletedTh || isSubcommitteeTypeEmpty}
            name={name}
            mode={mode}
            filteredSubcommittees={subcommitteesList}
            selectedSubcommittees={selectedSubcommittees}
            onCheckboxChange={handleCheckboxChange}
          />
          <InputOtherSectionSubcommittees
            name={otherName}
            title={`${otherTitle} (ถ้ามี)`}
            disabled={isOtherEnabled}
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
        </Box>
      </Flex>
    </Flex>
  );
};

export default SubcommitteesCheckboxList;
